// ==================== ADMIN DASHBOARD PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Building2
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/helpers';
import { ROUTES } from '../../constants/routes.constants';
import { 
  dashboardService, 
  type DashboardAnalytics,
  type ActivityItem 
} from '../../services';
import { RenewalAmounts } from '../../components/coupons/RenewalAmounts';
import { CommissionAmount } from '../../components/resellers/CommissionAmount';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// ==================== STAT CARD COMPONENT ====================

interface StatsCardProps {
  title: string;
  value: React.ReactNode;
  change: { value: number; type: 'positive' | 'negative' | 'neutral' };
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
  caption?: string;
}

const StatsCard = ({ title, value, change, icon: Icon, isLoading, caption }: StatsCardProps) => (
  <motion.div variants={fadeInUp}>
    <Card>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-(--color-muted-gray) mb-1">{title}</p>
          {isLoading ? (
            <div className="h-8 w-24 bg-(--color-navy-light) rounded animate-pulse" />
          ) : (
            <p className="text-2xl lg:text-3xl font-bold text-white font-heading">{value}</p>
          )}
          {caption && <p className="text-xs text-(--color-muted-gray) mt-2">{caption}</p>}
          {!isLoading && (
            <div className="flex items-center gap-1 mt-2">
              {change.type === 'positive' ? (
                <ArrowUpRight size={14} className="text-(--color-success)" />
              ) : change.type === 'negative' ? (
                <ArrowDownRight size={14} className="text-(--color-error)" />
              ) : null}
              <span
                className={cn(
                  'text-xs font-medium',
                  change.type === 'positive' && 'text-(--color-success)',
                  change.type === 'negative' && 'text-(--color-error)',
                  change.type === 'neutral' && 'text-(--color-muted-gray)'
                )}
              >
                {change.type !== 'neutral' ? `${change.value}%` : 'No change'}
              </span>
              <span className="text-xs text-(--color-muted-gray)">vs last month</span>
            </div>
          )}
        </div>
        <div className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center">
          <Icon className="text-(--color-signal-orange) w-6 h-6" />
        </div>
      </div>
    </Card>
  </motion.div>
);

// ==================== REVENUE CHART ====================

interface RevenueChartProps {
  data: Array<{ month: string; revenue: number }>;
  isLoading?: boolean;
}

const RevenueChart = ({ data, isLoading }: RevenueChartProps) => {
  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value}`;
  };

  return (
    <Card className="h-full">
      <CardHeader className="border-b-0 pb-2 mb-0">
        <div className="flex items-center justify-between">
          <CardTitle>Revenue Overview</CardTitle>
          <Badge variant="success">Last 12 months</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
          </div>
        ) : data.length === 0 ? (
          <div className="h-64 flex items-center justify-center border border-dashed border-(--color-border-gray) rounded-lg">
            <div className="text-center">
              <DollarSign size={48} className="text-(--color-muted-gray) mx-auto mb-2" />
              <p className="text-sm text-(--color-muted-gray)">No revenue data available</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F97316" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
              <XAxis 
                dataKey="month" 
                stroke="#6B7280" 
                tick={{ fill: '#9CA3AF', fontSize: 11 }}
                axisLine={{ stroke: '#374151' }}
              />
              <YAxis 
                stroke="#6B7280" 
                tick={{ fill: '#9CA3AF', fontSize: 11 }}
                tickFormatter={formatCurrency}
                axisLine={{ stroke: '#374151' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                }}
                itemStyle={{ color: '#fff' }}
                labelStyle={{ color: '#fff' }}
                formatter={(value) => [`$${(value as number).toFixed(2)}`, 'Revenue']}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#F97316"
                strokeWidth={2}
                fill="url(#revenueGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== CUSTOMER GROWTH CHART ====================

interface CustomerGrowthChartProps {
  data: Array<{ month: string; customers: number }>;
  isLoading?: boolean;
}

const CustomerGrowthChart = ({ data, isLoading }: CustomerGrowthChartProps) => (
  <Card className="h-full">
    <CardHeader className="border-b-0 pb-2 mb-0">
      <div className="flex items-center justify-between">
        <CardTitle>Customer Growth</CardTitle>
        <Badge variant="info">Last 12 months</Badge>
      </div>
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 flex items-center justify-center border border-dashed border-(--color-border-gray) rounded-lg">
          <div className="text-center">
            <Users size={48} className="text-(--color-muted-gray) mx-auto mb-2" />
            <p className="text-sm text-(--color-muted-gray)">No customer data available</p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis 
              dataKey="month" 
              stroke="#6B7280" 
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
              axisLine={{ stroke: '#374151' }}
            />
            <YAxis 
              stroke="#6B7280" 
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
              axisLine={{ stroke: '#374151' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
              }}
              itemStyle={{ color: '#fff' }}
              labelStyle={{ color: '#fff' }}
              formatter={(value) => [value as number, 'New Customers']}
            />
            <Bar dataKey="customers" fill="#F97316" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </CardContent>
  </Card>
);

// ==================== PLAN DISTRIBUTION CHART ====================

interface PlanDistributionChartProps {
  data: Array<{ name: string; count: number; color: string }>;
  isLoading?: boolean;
}

const PlanDistributionChart = ({ data, isLoading }: PlanDistributionChartProps) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card>
      <CardHeader className="border-b-0 pb-2 mb-0">
        <CardTitle>Subscription Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-48 flex items-center justify-center">
            <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
          </div>
        ) : data.length === 0 || total === 0 ? (
          <div className="h-48 flex items-center justify-center border border-dashed border-(--color-border-gray) rounded-lg">
            <div className="text-center">
              <CreditCard size={36} className="text-(--color-muted-gray) mx-auto mb-2" />
              <p className="text-sm text-(--color-muted-gray)">No subscriptions yet</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={180}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  itemStyle={{ color: '#fff' }}
                  labelStyle={{ color: '#fff' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {data.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-(--color-soft-gray)">{item.name}</span>
                  </div>
                  <span className="text-sm font-medium text-white">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== RECENT CUSTOMERS TABLE ====================

interface RecentCustomersProps {
  customers: Array<{
    id: string;
    name: string;
    email: string;
    plan: string;
    status: string;
    createdAt: string;
  }>;
  isLoading?: boolean;
}

const RecentCustomers = ({ customers, isLoading }: RecentCustomersProps) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <Badge variant="success">Active</Badge>;
      case 'PENDING':
        return <Badge variant="warning">Pending</Badge>;
      case 'REJECTED':
        return <Badge variant="error">Rejected</Badge>;
      case 'SUSPENDED':
        return <Badge variant="error">Suspended</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card padding="none">
      <div className="p-6 border-b border-(--color-border-gray)">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white font-heading">Recent Customers</h3>
          <Link 
            to={ROUTES.ADMIN.CUSTOMERS} 
            className="text-sm text-(--color-signal-orange) hover:underline"
          >
            View All
          </Link>
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12">
          <Building2 size={48} className="text-(--color-muted-gray) mx-auto mb-4" />
          <p className="text-(--color-muted-gray)">No customers yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-(--color-border-gray)">
                <th className="px-6 py-3 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="border-b border-(--color-border-gray) last:border-b-0 hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-white">{customer.name}</p>
                      <p className="text-xs text-(--color-muted-gray)">{customer.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="orange">{customer.plan}</Badge>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(customer.status)}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-(--color-muted-gray)">
                      {formatDate(customer.createdAt)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ==================== ACTIVITY FEED ====================

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

const ActivityFeed = ({ activities, isLoading }: ActivityFeedProps) => {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle size={14} className="text-(--color-success)" />;
      case 'warning':
        return <AlertCircle size={14} className="text-(--color-warning)" />;
      default:
        return <Clock size={14} className="text-(--color-info)" />;
    }
  };

  return (
    <Card>
      <CardHeader className="border-b-0 pb-2 mb-0">
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={24} className="text-(--color-signal-orange) animate-spin" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8">
            <Clock size={36} className="text-(--color-muted-gray) mx-auto mb-2" />
            <p className="text-sm text-(--color-muted-gray)">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.slice(0, 6).map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{activity.action}</p>
                  <p className="text-xs text-(--color-muted-gray)">
                    {formatTime(activity.time)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== MAIN ADMIN DASHBOARD COMPONENT ====================

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [analyticsResponse, activityResponse] = await Promise.all([
        dashboardService.getAnalytics(),
        dashboardService.getRecentActivity(10),
      ]);

      if (analyticsResponse.success && analyticsResponse.data) {
        setAnalytics(analyticsResponse.data);
      } else {
        setError(analyticsResponse.message || 'Failed to load analytics');
      }

      if (activityResponse.success && activityResponse.data) {
        setActivities(activityResponse.data);
      }
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const stats = analytics?.stats;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Dashboard</h1>
          <p className="text-(--color-muted-gray) mt-1">
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={fetchDashboardData}
          leftIcon={<RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </motion.div>

      {/* Error Alert */}
      {error && (
        <motion.div
          variants={fadeInUp}
          className="flex items-center gap-3 p-4 rounded-xl bg-(--color-error)/10 border border-(--color-error)/30"
        >
          <AlertCircle size={20} className="text-(--color-error)" />
          <p className="text-sm text-(--color-error)">{error}</p>
        </motion.div>
      )}

      {/* Stats Grid */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
      >
        <StatsCard
          title="Total Customers"
          value={stats?.totalCustomers?.toLocaleString() || '0'}
          change={stats?.customerChange || { value: 0, type: 'neutral' }}
          icon={Users}
          isLoading={isLoading}
        />
        <StatsCard
          title="Monthly Revenue"
          value={formatCurrency(stats?.monthlyRevenue || 0)}
          change={stats?.revenueChange || { value: 0, type: 'neutral' }}
          icon={DollarSign}
          isLoading={isLoading}
        />
        <StatsCard
          title="Active Subscriptions"
          value={stats?.activeSubscriptions?.toLocaleString() || '0'}
          change={stats?.subscriptionChange || { value: 0, type: 'neutral' }}
          icon={CreditCard}
          isLoading={isLoading}
        />
        <StatsCard
          title="Monthly MRR"
          caption="Expected renewal charges after recurring discounts"
          value={<RenewalAmounts amounts={stats?.mrrByCurrency} fallback={stats?.mrr} />}
          change={{ value: 0, type: 'neutral' }}
          icon={TrendingUp}
          isLoading={isLoading}
        />
      </motion.div>

      {/* Reseller commission: a payout cost, never part of revenue */}
      <motion.div variants={fadeInUp}>
        <Card>
          <CardHeader className="border-b-0 pb-2 mb-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>Reseller Commission</CardTitle>
                <p className="text-xs text-(--color-muted-gray) mt-1">Payout cost, shown separately from revenue</p>
              </div>
              <Link to={ROUTES.ADMIN.RESELLERS} className="text-sm text-(--color-signal-orange) hover:underline">
                Manage resellers
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {(['pending', 'paid'] as const).map((field) => (
                <div key={field}>
                  <p className="text-sm text-(--color-muted-gray)">{field === 'pending' ? 'Pending' : 'Paid'}</p>
                  <div className="text-xl text-white font-semibold">
                    {isLoading ? '—' : <CommissionAmount totals={stats?.resellerCommission} field={field} />}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Charts Row */}
      <motion.div variants={fadeInUp} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RevenueChart
            data={analytics?.monthlyRevenueTrend || []}
            isLoading={isLoading}
          />
        </div>
        <div>
          <ActivityFeed activities={activities} isLoading={isLoading} />
        </div>
      </motion.div>

      {/* Second Charts Row */}
      <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-6">
        <CustomerGrowthChart
          data={analytics?.customerGrowthTrend || []}
          isLoading={isLoading}
        />
        <PlanDistributionChart
          data={analytics?.subscriptionsByPlan || []}
          isLoading={isLoading}
        />
      </motion.div>

      {/* Recent Customers */}
      <motion.div variants={fadeInUp}>
        <RecentCustomers
          customers={analytics?.recentCustomers || []}
          isLoading={isLoading}
        />
      </motion.div>
    </motion.div>
  );
};

export { AdminDashboard };
